<script setup>
import {onMounted, nextTick, ref, computed, watch} from 'vue';
import {useRouter} from 'vue-router';
import ServiceRequirementService from '@/services/ServiceRequirementService.js';
import {formatDate, getCssVar, getContrastColor} from '@/helper';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const router = useRouter();

const unresolved = ref([]);
const resolved = ref([]);
const isLoading = ref(true);

const STORAGE_KEY = 'ADVISOR-SERVICE-REQUIREMENTS-TAB';

const activeTab = ref(localStorage.getItem(STORAGE_KEY) ?? 'active');
watch(activeTab, val => localStorage.setItem(STORAGE_KEY, val));

/**
 * Statistiky vypočítané z lokálních dat — nezávislé na globálním /stats endpointu,
 * který vrací počty přes celou databázi bez ohledu na poradce.
 */
const stats = computed(() => ({
    total: unresolved.value.length + resolved.value.length,
    open: unresolved.value.length,
    // Přímé požadavky na poradce jsou bez klienta
    direct: unresolved.value.filter(r => !r.client).length + resolved.value.filter(r => !r.client).length,
}));

function truncateText(text, max = 100) {
    if (!text) return '';
    return text.length > max ? text.slice(0, max) + '…' : text;
}

function datepickerFilterFn(data, filterString) {
    if (!filterString) return true;
    return data ? String(data).startsWith(filterString) : false;
}

/** Sloupce tabulky — navíc sloupec Klient oproti klientskému pohledu. */
const unresolvedColumns = ref([
    {
        label: t('advisorServiceRequirements.col_client'),
        field: 'client',
        sortable: false,
        filterOptions: {enabled: false},
    },
    {
        label: t('advisorServiceRequirements.col_text'),
        field: 'text',
        sortable: false,
        filterOptions: {enabled: false},
    },
    {
        label: t('advisorServiceRequirements.col_status'),
        field: 'status',
        sortable: true,
        filterOptions: {enabled: false},
    },
    {
        label: t('advisorServiceRequirements.col_created_at'),
        field: 'created_at',
        sortable: true,
        filterOptions: {enabled: true, datePicker: true, filterFn: datepickerFilterFn},
    },
    {
        label: '',
        field: 'action',
        sortable: false,
        width: '60px',
    },
]);

const resolvedColumns = ref([
    {
        label: t('advisorServiceRequirements.col_client'),
        field: 'client',
        sortable: false,
        filterOptions: {enabled: false},
    },
    {
        label: t('advisorServiceRequirements.col_text'),
        field: 'text',
        sortable: false,
        filterOptions: {enabled: false},
    },
    {
        label: t('advisorServiceRequirements.col_status'),
        field: 'status',
        sortable: true,
        filterOptions: {enabled: false},
    },
    {
        label: t('advisorServiceRequirements.col_resolved_at'),
        field: 'resolved_at',
        sortable: true,
        filterOptions: {enabled: true, datePicker: true, filterFn: datepickerFilterFn},
    },
    {
        label: t('advisorServiceRequirements.col_created_at'),
        field: 'created_at',
        sortable: true,
        filterOptions: {enabled: true, datePicker: true, filterFn: datepickerFilterFn},
    },
    {
        label: '',
        field: 'action',
        sortable: false,
        width: '60px',
    },
]);

onMounted(() => {
    fetchRequirements();
    nextTick(() => {
        const btns = tabGroupRef.value?.querySelectorAll('button');
        if (btns?.length) {
            const maxW = Math.max(...[...btns].map(b => b.scrollWidth));
            btns.forEach(b => (b.style.minWidth = maxW + 'px'));
        }
    });
});

const tabGroupRef = ref(null);

function fetchRequirements() {
    isLoading.value = true;
    ServiceRequirementService.getMyAdvisorRequirements().then(data => {
        unresolved.value = data.unresolved ?? [];
        resolved.value = data.resolved ?? [];
    }).finally(() => {
        isLoading.value = false;
    });
}

/** Přeloží hodnotu statusu do čitelného textu. */
function statusLabel(status) {
    return t('advisorServiceRequirements.status_' + status, status);
}

function statusBadgeStyle(status) {
    const bg1 = getCssVar('--primaryColor', '#004148');
    const bg2 = getCssVar('--primaryHoverColor', '#046e7a');
    const primary = { color: getContrastColor(bg1), backgroundColor: bg1, borderColor: bg1 };
    const hover   = { color: getContrastColor(bg2), backgroundColor: bg2, borderColor: bg2 };
    const light   = { color: bg1, backgroundColor: '#f0f2f4', borderColor: '#f0f2f4' };
    const map = {
        NEW: light,
        IN_PROGRESS: primary,
        WAITING_FOR_CLIENT: hover,
        WAITING_FOR_BO: light,
        CLOSED: primary,
    };
    return map[status] ?? light;
}

function goToDetail(id) {
    router.push({name: 'AdvisorServiceRequirementDetail', params: {id}});
}
</script>

<template>
    <div class="page-section">
        <div class="page-section__heading">
            <div class="row">
                <div class="col-3">
                    <a @click="router.back()" class="page-section__heading__back">
                        <i class="mdi mdi-arrow-left"></i> {{ t('advisorServiceRequirements.back') }}
                    </a>
                </div>
                <div class="col-6 text-center">
                    <div class="page-section__title">{{ t('advisorServiceRequirements.title') }}</div>
                </div>
            </div>
        </div>
    </div>

    <div class="page-section__content">
        <!-- Statistické dlaždice — počítány z dat tohoto poradce, ne globálně -->
        <div class="row gy-3 mb-4">
            <div class="col-12 col-md-4" v-for="tile in [
                { key: 'total',  label: t('advisorServiceRequirements.stat_total') },
                { key: 'open',   label: t('advisorServiceRequirements.stat_open') },
                { key: 'direct', label: t('advisorServiceRequirements.stat_direct') },
            ]" :key="tile.key">
                <div class="card card--big-value h-100">
                    <div class="card-body d-flex flex-column justify-content-center">
                        <div class="card__value__label">{{ tile.label }}</div>
                        <div class="card__value">
                            <Skeletor v-if="isLoading" width="40" height="28" />
                            <span v-else>{{ stats[tile.key] }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isLoading" class="text-center py-5">
            <span class="mdi mdi-loading mdi-spin me-2"></span>{{ t('advisorServiceRequirements.loading') }}
        </div>

        <template v-else>
            <!-- Tab přepínač -->
            <!-- Tab přepínač -->
            <div ref="tabGroupRef" class="d-flex gap-2 mb-4">
                <button
                    class="btn btn-normal"
                    :class="activeTab === 'active' ? 'btn-primary' : 'btn-outline-tenant'"
                    @click="activeTab = 'active'"
                >
                    {{ t('advisorServiceRequirements.tab_active') }}
                </button>
                <button
                    class="btn btn-normal"
                    :class="activeTab === 'resolved' ? 'btn-primary' : 'btn-outline-tenant'"
                    @click="activeTab = 'resolved'"
                >
                    {{ t('advisorServiceRequirements.tab_resolved') }}
                </button>
            </div>

            <!-- Tabulka aktivních požadavků -->
            <vue-good-table
                v-if="activeTab === 'active'"
                :columns="unresolvedColumns"
                :rows="unresolved"
                :pagination-options="{
                    enabled: true,
                    perPage: 10,
                    rowsPerPageLabel: t('advisorServiceRequirements.entriesPerPage'),
                    nextLabel: t('advisorServiceRequirements.next'),
                    prevLabel: t('advisorServiceRequirements.previous'),
                    ofLabel: 'z',
                }"
            >
                <template #column-filter="{ column, updateFilters }">
                    <input
                        v-if="column.filterOptions?.datePicker"
                        type="date"
                        class="vgt-input"
                        @change="updateFilters(column, $event.target.value)"
                    />
                </template>
                <template #table-row="props">
                    <!-- Klient nebo štítek pro přímý požadavek bez klienta -->
                    <span v-if="props.column.field === 'client'">
                        <span v-if="props.row.client">{{ props.row.client.label }}</span>
                        <span v-else class="badge bg-secondary">{{ t('advisorServiceRequirements.direct_request') }}</span>
                    </span>
                    <span v-else-if="props.column.field === 'text'" class="text-muted small">
                        {{ truncateText(props.row.text) }}
                    </span>
                    <span v-else-if="props.column.field === 'status'">
                        <span class="badge" :style="statusBadgeStyle(props.row.status)">
                            {{ statusLabel(props.row.status) }}
                        </span>
                    </span>
                    <span v-else-if="props.column.field === 'created_at'">
                        {{ formatDate(props.row.created_at, 'd.m.Y H:i') }}
                    </span>
                    <span v-else-if="props.column.field === 'action'" style="float: right">
                        <button type="button" class="btn btn-link btn-sm" :title="t('advisorServiceRequirements.detail')" @click="goToDetail(props.row.id)">
                            <i class="mdi mdi-eye"/>
                        </button>
                    </span>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
                <template v-slot:emptystate>
                    <div class="text-center">{{ t('advisorServiceRequirements.no_unresolved') }}</div>
                </template>
            </vue-good-table>

            <!-- Tabulka vyřešených požadavků -->
            <vue-good-table
                v-else
                :columns="resolvedColumns"
                :rows="resolved"
                :pagination-options="{
                    enabled: true,
                    perPage: 10,
                    rowsPerPageLabel: t('advisorServiceRequirements.entriesPerPage'),
                    nextLabel: t('advisorServiceRequirements.next'),
                    prevLabel: t('advisorServiceRequirements.previous'),
                    ofLabel: 'z',
                }"
            >
                <template #column-filter="{ column, updateFilters }">
                    <input
                        v-if="column.filterOptions?.datePicker"
                        type="date"
                        class="vgt-input"
                        @change="updateFilters(column, $event.target.value)"
                    />
                </template>
                <template #table-row="props">
                    <span v-if="props.column.field === 'client'">
                        <span v-if="props.row.client">{{ props.row.client.label }}</span>
                        <span v-else class="badge bg-secondary">{{ t('advisorServiceRequirements.direct_request') }}</span>
                    </span>
                    <span v-else-if="props.column.field === 'text'" class="text-muted small">
                        {{ truncateText(props.row.text) }}
                    </span>
                    <span v-else-if="props.column.field === 'status'">
                        <span class="badge" :style="statusBadgeStyle(props.row.status)">
                            {{ statusLabel(props.row.status) }}
                        </span>
                    </span>
                    <span v-else-if="props.column.field === 'resolved_at'">
                        {{ props.row.resolved_at ? formatDate(props.row.resolved_at, 'd.m.Y H:i') : '---' }}
                    </span>
                    <span v-else-if="props.column.field === 'created_at'">
                        {{ formatDate(props.row.created_at, 'd.m.Y H:i') }}
                    </span>
                    <span v-else-if="props.column.field === 'action'" style="float: right">
                        <button type="button" class="btn btn-link btn-sm" :title="t('advisorServiceRequirements.detail')" @click="goToDetail(props.row.id)">
                            <i class="mdi mdi-eye"/>
                        </button>
                    </span>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
                <template v-slot:emptystate>
                    <div class="text-center">{{ t('advisorServiceRequirements.no_resolved') }}</div>
                </template>
            </vue-good-table>
        </template>
    </div>
</template>

<style scoped>
.card__value {
  font-size: 2.5em;
}
.card__value__label {
  font-size: 1.1em !important;
  opacity: 1;
  min-height: unset;
}
</style>